import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Box, Chip, CircularProgress, IconButton, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, TableSortLabel } from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';

import { getProductsAction, getProductsState } from '../../../features/product/get_products';
import QRCodeDownload from '../../common/qr_code_download';
import { GET_ROUTE_PRODUCT_FORMS } from '../../../core/constants';
import useNotification from "../../../core/hooks/use_notification";
import { patchProductAction } from "../../../features/product/patch_product";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { updateProductStatusNotificationData } from "../../../core/utils/notification_utils";
import { ApiRequestStatusEnum } from "../../../core/enums/common/api";

const ProductsList = ({ queryParams, onSortChange, currentSort }) => {
    const getProducts = useSelector(getProductsState);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { showNotification } = useNotification();

    const [updatingStatuses, setUpdatingStatuses] = useState({});

    const sortField = currentSort ? currentSort.split(',')[0] : 'id';
    const sortDirection = currentSort ? currentSort.split(',')[1] : 'asc';

    const handleRequestSort = (property) => {
        const isAsc = sortField === property && sortDirection === 'asc';
        const newDirection = isAsc ? 'desc' : 'asc';
        onSortChange(`${property},${newDirection}`);
    };

    const onPatchProduct = (product) => {
        setUpdatingStatuses(prev => ({ ...prev, [product?.id]: true }));
        const command = product?.active ? 'DEACTIVATE' : 'ACTIVATE';

        dispatch(patchProductAction({ id: product?.id, command }))
            .then(response => {
                showNotification(updateProductStatusNotificationData(response))
                if (response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED) {
                    dispatch(getProductsAction(queryParams));
                }
            })
            .finally(() => {
                setUpdatingStatuses(prev => ({ ...prev, [product?.id]: false }));
            });
    };

    return (
        <TableContainer component={ Paper } variant="outlined">
            <Table stickyHeader>
                <TableHead>
                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                        <TableCell>
                            <TableSortLabel
                                active={sortField === 'id'}
                                direction={sortField === 'id' ? sortDirection : 'asc'}
                                onClick={() => handleRequestSort('id')}
                            >
                                ID
                            </TableSortLabel>
                        </TableCell>
                        <TableCell>
                            <TableSortLabel
                                active={sortField === 'workingName'}
                                direction={sortField === 'workingName' ? sortDirection : 'asc'}
                                onClick={() => handleRequestSort('workingName')}
                            >
                                Nazwa (robocza)
                            </TableSortLabel>
                        </TableCell>
                        <TableCell align="center">Produkt aktywny</TableCell>
                        <TableCell align="center">Wersje językowe</TableCell>
                        <TableCell align="right" sx={ { minWidth: 140 } }></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    { getProducts?.data?.content?.map(product => {
                        const isUpdating = updatingStatuses[product?.id] || false;

                        return (
                            <TableRow key={ product?.id } hover>
                                <TableCell sx={ { fontWeight: 600 } }>
                                    { product?.id }
                                </TableCell>
                                <TableCell>
                                    { product?.name }
                                </TableCell>
                                <TableCell align="center">
                                    <Chip
                                        label={ product?.active ? "TAK" : "NIE" }
                                        color={ product?.active ? "success" : "error" }
                                        size="small"
                                        variant="outlined"
                                    />
                                </TableCell>
                                <TableCell align="center">
                                    <Box sx={ {
                                        display: 'inline-flex',
                                        gap: 1,
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        flexWrap: 'wrap'
                                    } }>
                                        { Object.entries(product?.completedTranslationLanguages || {})
                                            .filter(([language, isCompleted]) => isCompleted === true)
                                            .map(([language]) => (
                                                <Chip
                                                    key={ language }
                                                    label={ language }
                                                    color="secondary"
                                                    size="small"
                                                    variant="outlined"
                                                />
                                            )) }
                                    </Box>
                                </TableCell>
                                <TableCell align="right">
                                    <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={ 1 }>

                                        <Tooltip title={ product?.active ? "Dezaktywuj" : "Aktywuj" }>
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    color={ product?.active ? 'error' : 'success' }
                                                    onClick={ () => onPatchProduct(product) }
                                                    disabled={ isUpdating }
                                                >
                                                    { isUpdating ? <CircularProgress size={ 20 } color="inherit"/> :
                                                        <PowerSettingsNewIcon fontSize="small"/> }
                                                </IconButton>
                                            </span>
                                        </Tooltip>

                                        <QRCodeDownload
                                            productId={ product?.id }
                                            productName={ product?.name }
                                        />

                                        <Tooltip title="Podgląd / Edycja">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={ () => navigate(GET_ROUTE_PRODUCT_FORMS(product?.id), { state: { queryParams } }) }
                                            >
                                                <EditIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </TableCell>
                            </TableRow>
                        )
                    }) }
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default ProductsList;